import { ReactNode, useContext, useEffect, useState } from 'react';
import { AppContext } from 'context/appContext';
import {
  Outlet,
  useOutletContext,
  ActionFunction,
  ActionFunctionArgs,
  useParams,
  useNavigate,
} from 'react-router';
import { useTranslation } from 'react-i18next';
import FormTabNavigation, {
  FormTab,
} from 'components/layout/FormTabNavigation';
import { ROUTES, routeWithParams } from 'router/routes';
import { Box } from '@mui/material';
import FormHeaderBar from 'components/layout/FormHeaderBar';
import {
  DisplayMode,
  FormMode,
  PanelRootContext,
  Tenant,
  Property,
  PropertyGroup,
  User,
  UserListItem,
  Contact,
} from 'types';
import { TraxeroActionResponse } from 'types';
import {
  checkDuplicateAccount,
  createUser,
  putPropertyUsers,
  updateUser,
  putPropertyGroupUsers,
  getPropertyUsers,
  getSuperUsers,
  getTenantUserList,
  softDeleteUser,
} from 'api/users';
import {
  createContact,
  getPropertyContacts,
  getPropertyGroupContacts,
  getTenantContacts,
  putPropertyContacts,
  putPropertyGroupContacts,
  updateContact,
} from 'api/contacts';
import AuthService from 'utils/AuthService';
import { logError } from 'utils';
import { getPropertyGroupUsers } from 'api/propertyGroup';
import { useFetcherKey } from 'hooks/useFetcherKey';

export interface PeopleTabLayoutRouteParams {
  params: {
    tenantID?: string | undefined;
    groupID?: string | undefined;
    propertyID?: string | undefined;
    userID?: string | undefined;
    contactID?: string | undefined;
  };
}
export interface PeopleTabLayoutLoaderData {
  _users: Promise<User[] | UserListItem[]>;
  _contacts: Promise<Contact[]>;
}

export interface PeopleTabLayoutContext {
  formMode: FormMode;
  setFormMode: (formMode: FormMode) => void;
  displayMode: DisplayMode;
  entity: Tenant | Property | PropertyGroup;
  fetcherKey: string;
}

export const loader = ({
  params,
}: PeopleTabLayoutRouteParams): PeopleTabLayoutLoaderData => {
  if (params.propertyID) {
    return {
      _users: getPropertyUsers({
        tenantID: params.tenantID,
        propertyID: params.propertyID,
        includeGroupUsers: true,
        includeInactive: AuthService.IsSuperAdmin(),
      }),
      _contacts: getPropertyContacts({
        tenantID: params.tenantID,
        propertyID: params.propertyID,
      }),
    };
  } else if (params.groupID) {
    return {
      _users: getPropertyGroupUsers({
        tenantID: params.tenantID,
        groupID: params.groupID,
      }),
      _contacts: getPropertyGroupContacts({
        tenantID: params.tenantID,
        groupID: params.groupID,
      }),
    };
  } else if (params.tenantID) {
    if (params.tenantID === '1' && AuthService.IsSuperAdmin()) {
      return {
        _users: getSuperUsers(),
        _contacts: getTenantContacts({
          tenantID: params.tenantID,
        }),
      };
    } else {
      return {
        _users: getTenantUserList(params.tenantID),
        _contacts: getTenantContacts({
          tenantID: params.tenantID,
        }),
      };
    }
  } else {
    return {
      _users: Promise.resolve([]),
      _contacts: Promise.resolve([]),
    };
  }
};

const PeopleTabLayout = (): ReactNode => {
  const { t } = useTranslation();
  const { state } = useContext(AppContext);
  const { userID, contactID } = useParams();
  const { formMode, setFormMode, displayMode, entity } =
    useOutletContext<PanelRootContext<Tenant | Property | PropertyGroup>>();
  const { fetcherKey } = useFetcherKey();
  const [tabs, setTabs] = useState<FormTab[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (displayMode && entity.id) {
      const _tabs: FormTab[] = [];    

      if (state.entityPermissions.user.read) {
        _tabs.push({
          label: `${t('user.label_plural', { ns: 'entities' })}`,
          route: getNavigationRouteForDisplayMode(
            'users',
            displayMode,
            state.tenant.id,
            entity.id,
          ),
          beforeNavigate: () => {
            setFormMode('none');
            return true;
          },
        });
      }

      if (state.entityPermissions.contact.read) {
        _tabs.push({
          label: `${t('contact.label_plural', { ns: 'entities' })}`,
          route: getNavigationRouteForDisplayMode(
            'contacts',
            displayMode,
            state.tenant.id,
            entity.id,
          ),
          beforeNavigate: () => {
            setFormMode('none');
            return true;
          },
        });
      }

      setTabs(_tabs);
      if (_tabs.length > 0) {
        navigate(_tabs[0].route);
      }
    }
  }, [displayMode, state.tenant.id, entity.id]);

  return (
    <Box
      flexGrow={1}
      display={'flex'}
      flexDirection={'column'}
      minHeight={0}
      sx={{
        overflow: 'hidden',
      }}
    >
      <FormHeaderBar />
      <FormTabNavigation
        tabs={tabs}
        title={t('tabMenu.people')}
        hidden={!!userID || !!contactID}
      />

      <Box
        flexGrow={1}
        minHeight={0}
        sx={{
          overflow: 'hidden',
        }}
      >
        <Outlet
          context={
            {
              formMode,
              setFormMode,
              entity,
              displayMode,
              fetcherKey,
            } satisfies PeopleTabLayoutContext
          }
        />
      </Box>
    </Box>
  );
};

export interface GetActionRouteForDisplayModeParams {
  displayMode: DisplayMode;
  propertyID: string | number;
  groupID: string | number;
  tenantID: string | number;
}
export const getActionRouteForDisplayMode = ({
  displayMode,
  propertyID,
  groupID,
  tenantID,
}: GetActionRouteForDisplayModeParams): string => {
  switch (displayMode) {
    case 'property':
      return routeWithParams(ROUTES.PROPERTY_PEOPLE, [tenantID, propertyID]);
    case 'group':
      return routeWithParams(ROUTES.PROPERTY_GROUP_PEOPLE, [tenantID, groupID]);
    case 'tenant':
      return routeWithParams(ROUTES.PEOPLE, [tenantID]);
    default:
      return routeWithParams(ROUTES.PEOPLE, [tenantID]);
  }
};

export const getLoaderRouteForDisplayMode = (
  displayMode: DisplayMode,
): ROUTES => {
  switch (displayMode) {
    case 'property':
      return ROUTES.PROPERTY_PEOPLE;
    case 'group':
      return ROUTES.PROPERTY_GROUP_PEOPLE;
    case 'tenant':
      return ROUTES.PEOPLE;
    default:
      return ROUTES.PEOPLE;
  }
};

const getNavigationRouteForDisplayMode = (
  tab: 'contacts' | 'users',
  displayMode: DisplayMode,
  tenantID: string | number,
  entityID: string | number,
): string => {
  switch (`${tab}-${displayMode}`) {
    case 'contacts-tenant':
      return routeWithParams(ROUTES.CONTACTS, [tenantID]);
    case 'users-tenant':
      return routeWithParams(ROUTES.USERS, [tenantID]);
    case 'contacts-property':
      return routeWithParams(ROUTES.PROPERTY_CONTACTS, [tenantID, entityID]);
    case 'users-property':
      return routeWithParams(ROUTES.PROPERTY_USERS, [tenantID, entityID]);
    case 'contacts-group':
      return routeWithParams(ROUTES.PROPERTY_GROUP_CONTACTS, [
        tenantID,
        entityID,
      ]);
    case 'users-group':
      return routeWithParams(ROUTES.PROPERTY_GROUP_USERS, [
        tenantID,
        entityID,
      ]);
    default:
      return '';
  }
};

export enum PeopleTabActionHandler {
  TENANT_CONTACT_UPDATE = 'tenant_contact_update',
  PROPERTY_CONTACT_UPDATE = 'property_contact_update',
  PROPERTY_GROUP_CONTACT_UPDATE = 'property_group_contact_update',

  TENANT_CONTACT_CREATE = 'tenant_contact_create',
  PROPERTY_CONTACT_CREATE = 'property_contact_create',
  PROPERTY_GROUP_CONTACT_CREATE = 'property_group_contact_create',

  PROPERTY_CONTACT_UPSERT = 'property_contact_upsert',
  PROPERTY_GROUP_CONTACT_UPSERT = 'property_group_contact_upsert',

  TENANT_USER_UPDATE = 'tenant_user_update',
  PROPERTY_USER_UPDATE = 'property_user_update',
  PROPERTY_GROUP_USER_UPDATE = 'property_group_user_update',

  TENANT_USER_CREATE = 'tenant_user_create',
  PROPERTY_USER_CREATE = 'property_user_create',
  PROPERTY_USER_UPSERT = 'property_user_upsert',

  PROPERTY_GROUP_USER_CREATE = 'property_group_user_create',
  PROPERTY_GROUP_USER_UPSERT = 'property_group_user_upsert',

  CHECK_DUPLICATE_ACCOUNT = 'check_duplicate_account',

  CONTACT_DELETE = 'contact_delete',
  USER_DELETE = 'user_delete',

  UNKNOWN = 'unknown',
}

export const action: ActionFunction = async ({
  request,
}: ActionFunctionArgs): Promise<
  TraxeroActionResponse<PeopleTabActionHandler>
> => {
  const { handler, body, tenantID, entityID, userID, contactID, email, token } =
    await request.json();

  if (handler === PeopleTabActionHandler.UNKNOWN) {
    return {
      handler,
      status: 500,
      statusText: 'unknown handler',
    };
  }

  let res: Response;
  try {
    switch (handler) {
      case PeopleTabActionHandler.CONTACT_DELETE:
      case PeopleTabActionHandler.TENANT_CONTACT_UPDATE:
      case PeopleTabActionHandler.PROPERTY_CONTACT_UPDATE:
      case PeopleTabActionHandler.PROPERTY_GROUP_CONTACT_UPDATE:
        res = await updateContact({
          tenantID,
          contactID,
          body,
        });
        break;
      case PeopleTabActionHandler.TENANT_CONTACT_CREATE:
        res = await createContact(body);
        break;
      case PeopleTabActionHandler.PROPERTY_CONTACT_CREATE:
      case PeopleTabActionHandler.PROPERTY_CONTACT_UPSERT:
        res = await putPropertyContacts({
          tenantID,
          propertyID: entityID,
          body,
        });
        break;
      case PeopleTabActionHandler.PROPERTY_GROUP_CONTACT_CREATE:
      case PeopleTabActionHandler.PROPERTY_GROUP_CONTACT_UPSERT:
        res = await putPropertyGroupContacts({
          tenantID,
          groupID: entityID,
          body,
        });
        break;
      case PeopleTabActionHandler.USER_DELETE:
        res = await softDeleteUser(userID);
        break;
      case PeopleTabActionHandler.TENANT_USER_UPDATE:
      case PeopleTabActionHandler.PROPERTY_USER_UPDATE:
      case PeopleTabActionHandler.PROPERTY_GROUP_USER_UPDATE:
        res = await updateUser({
          userID,
          body,
        });
        break;
      case PeopleTabActionHandler.TENANT_USER_CREATE:
        res = await createUser(body);
        break;
      case PeopleTabActionHandler.PROPERTY_USER_CREATE:
      case PeopleTabActionHandler.PROPERTY_USER_UPSERT:
        res = await putPropertyUsers({
          tenantID,
          propertyID: entityID,
          body,
        });
        break;
      case PeopleTabActionHandler.PROPERTY_GROUP_USER_CREATE:
      case PeopleTabActionHandler.PROPERTY_GROUP_USER_UPSERT:
        res = await putPropertyGroupUsers({
          tenantID,
          groupID: entityID,
          body,
        });
        break;
      case PeopleTabActionHandler.CHECK_DUPLICATE_ACCOUNT: {
        res = await checkDuplicateAccount({ email, token });
        const response = await res.json();
        if (response.activeAccount) {
          return {
            handler,
            status: res.status,
            statusText: 'Email Already Exists, please choose a different email',
          };
        }
        res = await updateUser({ body, userID });
        break;
      }
      default: {
        res = new Response(null, { status: 400, statusText: 'Bad Request' });
      }
    }
  } catch (error) {
    logError(error as Error);
    return {
      handler,
      status: 500,
      statusText: 'Internal Server Error',
    };
  }

  let statusText = res.statusText;

  // Extract error message from response body for better user feedback
  if (!res.ok) {
    try {
      const errorBody = await res.json();
      if (errorBody?.message) {
        statusText = errorBody.message;
      }
    } catch (error) {
      // If we can't parse the response body, fall back to statusText
      // eslint-disable-next-line no-console
      console.warn('Failed to parse error response body:', error);
    }
  }

  const actionRes: TraxeroActionResponse<PeopleTabActionHandler> = {
    handler,
    status: res.status,
    statusText,
  };
  return actionRes;
};

PeopleTabLayout.displayName = 'PeopleTabLayout';
export default PeopleTabLayout;
